import { useState } from "react";
import type { FormEvent } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { adminLogout, useAuth } from "@/hooks/useAuth";

const NAV_ITEMS = [
  { to: "/", label: "数据总览", end: true },
  { to: "/domains", label: "域名管理", end: false },
  { to: "/analytics", label: "访问统计", end: false },
  { to: "/settings", label: "系统设置", end: false },
];

export default function Topbar({ title }: { title: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/domains?q=${encodeURIComponent(query)}` : "/domains");
  };

  const handleLogout = () => {
    void adminLogout().then(() => navigate("/login"));
  };

  return (
    <header className="sticky top-0 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex items-center justify-between gap-4 px-4 py-3.5 md:px-8">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 lg:hidden cursor-pointer"
            aria-label="菜单"
          >
            <i className="ri-menu-2-line text-xl" />
          </button>
          <div>
            <h1 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">{title}</h1>
            <p className="hidden text-xs text-foreground-500 sm:block">域名中转 · 跳转规则集中管理</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <form
            onSubmit={handleSearch}
            className="hidden items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-600 focus-within:border-primary-300 md:flex"
          >
            <i className="ri-search-line" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索域名…"
              className="w-36 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
          </form>
          <div className="flex items-center gap-2 rounded-full bg-background-100 py-1 pl-1 pr-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-background-50">
              {user?.username?.slice(0, 1).toUpperCase() ?? "A"}
            </span>
            <div className="hidden leading-tight sm:block">
              <p className="text-xs font-medium text-foreground-900">{user?.username ?? "admin"}</p>
              <p className="text-[10px] text-foreground-500">管理员</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="ml-1 flex h-7 w-7 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200 hover:text-primary-700 cursor-pointer"
              aria-label="退出登录"
            >
              <i className="ri-logout-box-r-line" />
            </button>
          </div>
        </div>
      </div>

      {menuOpen ? (
        <nav className="flex flex-col gap-1 border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `rounded-md px-3 py-2.5 text-sm cursor-pointer ${
                  isActive ? "bg-primary-500 font-medium text-background-50" : "text-foreground-700 hover:bg-background-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      ) : null}
    </header>
  );
}